import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Upload, Users, Camera, Loader2, Play, Copy, Check,
  Hash, Music, Zap, Instagram, Share2, ArrowRight, Plus, Trash2, Home
} from "lucide-react";
import AIImageWithDisclaimer from "../components/AIImageWithDisclaimer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OCCASIONS = ["Birthday", "Anniversary", "House Party", "Pre-wedding", "Bachelor/ette", "Baby Shower", "Housewarming"];

const PlanWithFriends = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState("form"); // form | generating | result
  const [form, setForm] = useState({
    budget: 6000, friends_count: 4, occasion: "Birthday", city: "Mumbai",
  });
  const [roomImage, setRoomImage] = useState(null);   // { base64, name, previewUrl }
  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [magicJobId, setMagicJobId] = useState(null);
  const [aiImage, setAiImage] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const handleImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setRoomImage({ base64: r.result, name: f.name, previewUrl: r.result });
    r.readAsDataURL(f);
  };

  const generate = async () => {
    setError(""); setStep("generating");
    try {
      const { data } = await axios.post(`${API_URL}/api/party-vibes/plan`, {
        ...form,
        image_base64: roomImage?.base64 || null,
        generate_image: !!roomImage,
      });
      setPlan(data.plan);
      setPlanId(data.plan_id);
      setMagicJobId(data.magic_job_id);
      setStep("result");
      // Poll AI image if requested
      if (data.magic_job_id) pollMagic(data.magic_job_id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Plan generation failed");
      setStep("form");
    }
  };

  const pollMagic = useCallback(async (jobId) => {
    // Fast-first poll: fire immediately (no upfront sleep), then tight 1.2s
    // cadence for the first ~10s (covers cache hits + fast-queue completions),
    // then relax to 2.5s. Previously the loop waited 3s BEFORE the first fetch
    // which added a hardcoded 3s minimum delay even when the job was already
    // completed (admin sees it instantly because it reads directly).
    const DELAYS_MS = [
      0, 1200, 1200, 1200, 1200, 1200, 1200, 1200, 1200,     // 0-10s tight
      2500, 2500, 2500, 2500, 2500, 2500, 2500, 2500, 2500,  // 10-32s medium
      4000, 4000, 4000, 4000, 4000, 4000, 4000, 4000, 4000,  // 32-68s slow
      4000, 4000, 4000, 4000, 4000, 4000,                     // 68-92s cap
    ];
    for (let i = 0; i < DELAYS_MS.length; i++) {
      if (DELAYS_MS[i] > 0) await new Promise(r => setTimeout(r, DELAYS_MS[i]));
      try {
        const { data } = await axios.get(`${API_URL}/api/magic/status/${jobId}`);
        if (data.status === "completed") {
          setAiImage(data.image_with_details_base64 || data.image_base64);
          return;
        }
        if (data.status === "failed") return; // backend already surfaced the error
      } catch (e) {
        // Transient network / 404 mid-poll — keep polling but surface
        // via debug log so we see runaway polls in dev tools.
        console.debug("[PlanWithFriends] poll miss:", e?.message);
      }
    }
  }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key); setTimeout(() => setCopied(""), 1500);
  };

  const startSplitPay = () => {
    navigate(`/split-pay?plan_id=${planId}&budget=${plan.budget_split.total}&friends=${plan.budget_split.friends_count}&occasion=${encodeURIComponent(plan.occasion || form.occasion)}&city=${encodeURIComponent(form.city)}`);
  };

  /* ───── Generating state ───── */
  if (step === "generating") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1E1B4B] via-[#3b0764] to-[#831843] flex items-center justify-center p-6">
        <div className="text-center text-white max-w-md">
          <div className="inline-block p-6 rounded-full bg-white/10 backdrop-blur mb-4">
            <Loader2 className="animate-spin" size={56} />
          </div>
          <h2 className="text-3xl font-bold font-['Outfit'] mb-2">Cooking up your vibe ✨</h2>
          <p className="text-white/70 text-sm">Our Gen Z AI is curating decor, reel ideas and captions tailored to your budget…</p>
        </div>
      </div>
    );
  }

  /* ───── Result state ───── */
  if (step === "result" && plan) {
    const b = plan.budget_split || {};
    return (
      <div className="min-h-screen bg-[#FFFAF5]" data-testid="party-plan-result">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#E83E8C] via-[#FF9F1C] to-[#8B5CF6] px-5 py-10 text-white">
          <button onClick={() => navigate("/")} className="text-white/80 text-xs mb-4 flex items-center gap-1"><Home size={12}/> DoozyGo</button>
          <div className="flex items-center gap-2 mb-2"><Sparkles size={20}/><span className="uppercase text-xs tracking-wider font-semibold opacity-80">Your Party Vibe</span></div>
          <h1 className="text-4xl sm:text-5xl font-black font-['Outfit'] leading-tight">
            {plan.vibe?.emoji} {plan.vibe?.title}
          </h1>
          <p className="mt-3 text-lg opacity-90 leading-snug">{plan.vibe?.tagline}</p>
        </div>

        <div className="p-5 space-y-5 max-w-3xl mx-auto">
          {/* Budget split hero card */}
          <div className="bg-white rounded-2xl p-5 shadow-xl border-2 border-[#E83E8C]/20" data-testid="budget-split-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#E83E8C] font-bold mb-1">💸 Budget Split</p>
                <p className="text-3xl font-black text-[#1E1B4B] font-['Outfit']">₹{b.per_head}<span className="text-base text-gray-400 font-semibold"> / friend</span></p>
                <p className="text-xs text-gray-500 mt-1">{b.pitch}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Total</p>
                <p className="text-2xl font-bold text-[#1E1B4B]">₹{b.total}</p>
              </div>
            </div>
            <button
              onClick={startSplitPay}
              className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-[#E83E8C] to-orange-500 text-white font-bold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
              data-testid="split-pay-cta"
            >
              <Users size={18}/> Split with Friends & Pay <ArrowRight size={16}/>
            </button>
            <p className="text-[11px] text-gray-400 mt-2 text-center">Sab milke contribute karein → DoozyGo books it 🎉</p>
          </div>

          {/* AI Image Preview */}
          {roomImage && (
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="grid grid-cols-2 gap-0">
                <div className="relative">
                  <img src={roomImage.previewUrl} alt="before" className="w-full h-full object-cover aspect-square"/>
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-black/50 text-white text-[10px] font-bold">BEFORE</div>
                </div>
                <div className="relative bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] flex items-center justify-center aspect-square">
                  {aiImage ? (
                    <AIImageWithDisclaimer wrapperClassName="w-full h-full" src={`data:image/jpeg;base64,${aiImage}`} alt="after" className="w-full h-full object-cover" compact />
                  ) : (
                    <div className="text-center text-white p-4">
                      <Loader2 className="animate-spin mx-auto mb-2"/>
                      <p className="text-xs">AI vibe preview loading…</p>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-[#E83E8C] text-white text-[10px] font-bold">AFTER ✨</div>
                </div>
              </div>
            </div>
          )}

          {/* Decor list */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Zap size={16} className="text-[#E83E8C]"/> The Decor Kit</h3>
            <div className="space-y-2">
              {(plan.decor_list || []).map((d, i) => (
                <div key={`${d.item}-${i}`} className="flex items-center justify-between py-2 border-b last:border-0 border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${d.diy ? "bg-[#8B5CF6]" : "bg-[#E83E8C]"}`}/>
                    <span className="text-sm text-[#1E1B4B] font-medium">{d.item}</span>
                    {d.diy && <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold">DIY</span>}
                  </div>
                  <span className="text-sm font-bold text-[#1E1B4B]">₹{d.price}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Viral moments */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Instagram size={16} className="text-[#E83E8C]"/> Viral Moments</h3>
            {(plan.viral_moments || []).map((m, i) => (
              <div key={`${m.title}-${i}`} className="mb-3 last:mb-0 p-3 rounded-xl bg-gradient-to-r from-pink-50 to-orange-50">
                <p className="font-bold text-sm text-[#1E1B4B]">{m.title}</p>
                <p className="text-xs text-gray-600 mt-1">{m.detail}</p>
              </div>
            ))}
          </div>

          {/* Reel plan */}
          {plan.reel_plan && (
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Play size={16} className="text-[#E83E8C]"/> Reel Plan</h3>
              <div className="mb-3 p-3 rounded-xl bg-[#1E1B4B] text-white">
                <p className="text-[11px] uppercase text-white/60 font-semibold mb-1">HOOK (first 3 sec)</p>
                <p className="text-sm font-semibold">{plan.reel_plan.hook}</p>
              </div>
              <p className="text-xs uppercase text-gray-400 font-semibold mb-2">Shot Sequence</p>
              <ol className="space-y-1 mb-3">
                {(plan.reel_plan.shots || []).map((s, i) => (
                  <li key={`shot-${i}`} className="text-xs text-gray-700 flex gap-2"><span className="text-[#E83E8C] font-bold">{i+1}.</span>{s}</li>
                ))}
              </ol>
              <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-100">
                <Music size={12}/> <span>{plan.reel_plan.music}</span>
              </div>
            </div>
          )}

          {/* Captions */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Share2 size={16} className="text-[#E83E8C]"/> Ready-to-Post Captions</h3>
            {(plan.captions || []).map((c, i) => (
              <div key={`caption-${i}`} className="flex items-start gap-2 mb-2 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition">
                <p className="flex-1 text-sm text-[#1E1B4B]">{c}</p>
                <button onClick={() => copy(c, `cap-${i}`)} className="text-gray-400 hover:text-[#E83E8C]" data-testid={`copy-caption-${i}`}>
                  {copied === `cap-${i}` ? <Check size={14}/> : <Copy size={14}/>}
                </button>
              </div>
            ))}
          </div>

          {/* Hashtags */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Hash size={16} className="text-[#E83E8C]"/> Hashtags</h3>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(plan.hashtags || []).map((h) => (
                <span key={h} className="text-xs bg-[#FFF0F6] text-[#E83E8C] px-2 py-1 rounded-full font-semibold">{h}</span>
              ))}
            </div>
            <button onClick={() => copy((plan.hashtags||[]).join(" "), "tags")} className="text-xs text-gray-400 hover:text-[#E83E8C] flex items-center gap-1">
              {copied === "tags" ? <Check size={12}/> : <Copy size={12}/>} Copy all
            </button>
          </div>

          {/* Share line + DoozyGo® CTA */}
          <div className="bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] rounded-2xl p-5 text-white text-center">
            <p className="text-lg font-bold mb-2">{plan.share_line}</p>
            <p className="text-sm opacity-90">{plan.doozygo_cta}</p>
          </div>

          <button
            onClick={startSplitPay}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[#E83E8C] to-orange-500 text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg"
            data-testid="final-split-pay-cta"
          >
            <Users size={20}/> Let's Split the Vibe & Book It
          </button>

          <button onClick={() => { setStep("form"); setPlan(null); }} className="w-full py-2 text-xs text-gray-400 hover:text-[#E83E8C]">
            ← Start over with a new budget
          </button>
        </div>
      </div>
    );
  }

  /* ───── Form state ───── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFAF5] via-white to-[#fef3f2]" data-testid="party-plan-form">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1E1B4B] via-[#3b0764] to-[#831843] px-5 py-12 text-white">
        <button onClick={() => navigate("/")} className="text-white/80 text-xs mb-6 flex items-center gap-1"><Home size={12}/> DoozyGo</button>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-[#FF9F1C]"/>
          <span className="text-xs uppercase tracking-widest font-semibold opacity-80">AI Party Planner</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black font-['Outfit'] leading-tight">
          Plan a premium party,<br/>
          <span className="bg-gradient-to-r from-[#FF9F1C] to-[#E83E8C] bg-clip-text text-transparent">split with friends.</span>
        </h1>
        <p className="mt-4 text-base opacity-80 leading-relaxed max-w-md">
          Gen Z vibes, budget-friendly decor, Instagram-ready setup — AI ne design kiya, sab milke contribute karo 💸
        </p>
      </div>

      {/* Form */}
      <div className="max-w-xl mx-auto p-5 -mt-8 relative z-10 space-y-4">
        <div className="bg-white rounded-2xl p-5 shadow-xl border border-[#E83E8C]/10">
          {/* Room image */}
          <label className="block text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Upload your room (optional — unlocks AI preview)</label>
          {!roomImage ? (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full py-8 rounded-2xl border-2 border-dashed border-[#E83E8C]/30 flex flex-col items-center gap-2 text-[#E83E8C] hover:bg-pink-50 transition"
              data-testid="upload-room-btn"
            >
              <Camera size={28}/>
              <span className="text-sm font-semibold">Tap to add room photo</span>
              <span className="text-[10px] text-gray-400">Optional · unlocks AI transformation</span>
            </button>
          ) : (
            <div className="relative rounded-2xl overflow-hidden">
              <img src={roomImage.previewUrl} alt="room" className="w-full h-48 object-cover"/>
              <button onClick={() => setRoomImage(null)} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70">
                <Trash2 size={14}/>
              </button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="upload-room-input"/>

          {/* Occasion */}
          <label className="block text-xs uppercase tracking-wider text-gray-500 font-bold mt-5 mb-2">What's the occasion?</label>
          <div className="flex flex-wrap gap-1.5">
            {OCCASIONS.map(o => (
              <button
                key={o}
                type="button"
                onClick={() => setForm({ ...form, occasion: o })}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${form.occasion === o ? "bg-[#E83E8C] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid={`occasion-${o.replace(/[^a-z]/gi,'').toLowerCase()}`}
              >{o}</button>
            ))}
          </div>

          {/* Budget */}
          <label className="block text-xs uppercase tracking-wider text-gray-500 font-bold mt-5 mb-2">Total party budget</label>
          <div className="flex items-center gap-3">
            <input
              type="range" min="3000" max="15000" step="500"
              value={form.budget} onChange={e => setForm({ ...form, budget: parseInt(e.target.value) })}
              className="flex-1 accent-[#E83E8C]"
              data-testid="budget-slider"
            />
            <span className="text-lg font-black text-[#1E1B4B] min-w-[90px] text-right">₹{form.budget.toLocaleString()}</span>
          </div>

          {/* Friends */}
          <label className="block text-xs uppercase tracking-wider text-gray-500 font-bold mt-5 mb-2">Number of friends (incl. you)</label>
          <div className="flex items-center gap-3">
            <button onClick={() => setForm({ ...form, friends_count: Math.max(2, form.friends_count - 1) })} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-bold" data-testid="friends-minus">−</button>
            <div className="flex-1 text-center">
              <p className="text-3xl font-black text-[#1E1B4B]">{form.friends_count}</p>
              <p className="text-[10px] text-gray-400">= ₹{Math.round(form.budget / form.friends_count).toLocaleString()} per friend</p>
            </div>
            <button onClick={() => setForm({ ...form, friends_count: Math.min(15, form.friends_count + 1) })} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-bold" data-testid="friends-plus"><Plus size={16}/></button>
          </div>

          {/* City */}
          <label className="block text-xs uppercase tracking-wider text-gray-500 font-bold mt-5 mb-2">City</label>
          <input
            type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E83E8C]"
            placeholder="Mumbai, Bangalore, Delhi…"
            data-testid="city-input"
          />

          {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

          <button
            onClick={generate}
            className="mt-5 w-full py-4 rounded-xl bg-gradient-to-r from-[#E83E8C] to-orange-500 text-white font-bold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition"
            data-testid="generate-plan-btn"
          >
            <Sparkles size={18}/> Generate My Party Plan
          </button>
          <p className="text-[10px] text-gray-400 text-center mt-2">Powered by DoozyGo® Magic · ~15 sec to generate</p>
        </div>
      </div>
    </div>
  );
};

export default PlanWithFriends;
